// Modules
import React, { Component } from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import compose from 'lodash/flowRight';
import { connect } from 'react-redux';
import { asyncConnect } from 'redux-connect';
import { getLang } from 'redux-pagan';
import browserHistory from 'react-router/lib/browserHistory';
import cx from 'classnames';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { Element } from 'react-scroll';
// Components
import Modal from '../../components/Modal/Modal';
import ProfessionalContactUs from '../../components/ProfessionalContactUs/ProfessionalContactUs';
import CompanyInfoBlock from '../../components/Company/CompanyInfoBlock/CompanyInfoBlock';
import NotificationNoCompany from '../../components/NotificationNoCompany/NotificationNoCompany';
import ProfessionalsList from '../../components/ProfessionalsList/ProfessionalsList';
import ProfessionalsPageSearchInput from '../../components/ProfessionalsPageSearchInput/ProfessionalsPageSearchInput';
import RealtorsAssociationContainer from '../../containers/RealtorsAssociationContainer/RealtorsAssociationContainer';
// Helpers
import {
  shareUrl,
  language,
  getProfessionalsTypes,
  PROFESSIONALS_TYPES,
  ROUTES
} from '../../constants';
import appendLocale from '../../helpers/appendLocale';
// Redux
import {
  clearProfessionals, closeProfessionalsContactModal,
  openProfessionalsContactModal,
  receiveProfessionals, sendProfessionalsContactUsForm
} from '../../redux/modules/professionals';
import { receiveSuggestions } from '../../redux/modules/receiveSuggestions';
import { searchAddress } from '../../redux/modules/searchAddressLocus';
// Styles
import styles from './ProfessionalsPage.scss';
import checkRtlLanguage from '../../utils/checkRtlLanguage';
import { openMessageModal } from '../../redux/modules/messageModal';
import BrokerCommunityContainer, {
  BROKER_COMPANY_NAME,
  BROKER_COMPANY_PHONE,
  BROKER_COMPANY_LOGO
} from '../../containers/BrokerCommunityContainer/BrokerCommunityContainer';
import { getProfessionalDescription } from './descriptions';
import DescriptionTextBlock
  from '../../components/DescriptionTextBlock/DescriptionTextBlock';
import OMDropdown from '../../components/OMComponents/OMDropdown/OMDropdown';
import withPageWrapper from '../../containers/PageContainer/PageContainer';
import buildProfessionalsQuery from '../../utils/buildProfessionalsRequest';

import { REALTORS_ASSOCIATION_LOGO } from '../../containers/RealtorsAssociationContainer/constants';

const limit = 25;

class ProfessionalsPage extends Component {
  constructor(props) {
    super(props);

    const companiesTypes = getProfessionalsTypes(props.translate);

    this.throttledCalcSwitchButtonsCount = debounce(this.calcSwitchButtonsCount, 100);

    this.query = {
      $skip: 0,
      $limit: limit
    };

    this.state = {
      captcha: null,
      companyNameForFilter: '',
      currentCompanyType: get(props, 'location.query.type', PROFESSIONALS_TYPES.AGENCY),
      companiesTypes,
      maxSwitchButtonsCount: companiesTypes.length + 1
    };
  }

  componentWillMount() {
    this.query = buildProfessionalsQuery(this.props.location);

    // SSR's asyncConnect entry (below) already dispatched receiveProfessionals for this
    // exact query - skip the redundant refetch. If SSR failed/timed out, professionalsLoaded
    // is still false here and this fetches normally (client-only routes hit this too).
    if (!this.props.professionalsLoaded) {
      this.loadProfessionals(this.query);
    }
  }

  componentDidMount() {
    if (!this.props.isMobile) {
      this.calcSwitchButtonsCount();
      window.addEventListener('resize', this.throttledCalcSwitchButtonsCount);
    }
  }

  componentWillReceiveProps(newProps) {
    const isBrokerCommunity = this.checkIsBrokerCommunity(newProps);
    const isProfessionalsPage = this.checkIsProfessionals(newProps);
    const isRealtorsAssociation = this.checkIsRealtorsAssociation(newProps);

    const isLocaleChanged = (this.props.locale !== newProps.locale);
    const isCityChanged = (
      this.props.location.query.professionals_city !== newProps.location.query.professionals_city
    );
    const isPropertyCityChanged = (this.props.location.query.property_city !== newProps.location.query.property_city);
    const isPageChanged = (this.checkIsBrokerCommunity() !== isBrokerCommunity)
      || (this.checkIsRealtorsAssociation() !== isRealtorsAssociation);
    const shouldLoadProfessionals = (isPageChanged
      && (isBrokerCommunity || isProfessionalsPage || isRealtorsAssociation));

    if (isPageChanged) {
      this.query = {
        ...this.query,
        $skip: 0
      };
    }

    if (shouldLoadProfessionals) {
      this.props.clearProfessionals();
    }

    if (isLocaleChanged) {
      this.setState({ companiesTypes: getProfessionalsTypes(newProps.translate) });
    }

    if ((isLocaleChanged && this.props.location.query.professionals_city) || isCityChanged
      || shouldLoadProfessionals || isPropertyCityChanged) {
      const { professionals_city, property_city } = newProps.location.query;
      const query = professionals_city
        ? { city: professionals_city }
        : { city: undefined };

      this.query = {
        ...this.query,
        $skip: 0,
        $limit: limit,
        ...(professionals_city || property_city ? { $skip: undefined, $limit: undefined } : {}),
        property_city: property_city || undefined,
        ...query,
        is_community_member: isBrokerCommunity || undefined,
        is_association_member: isRealtorsAssociation || undefined,
        type: this.state.currentCompanyType
      };
      this.loadProfessionals(this.query);

      const searchQuery = {
        locale: (newProps.locale === 'fr' ? 'en' : newProps.locale), // TODO change this when locus supports French address
        type: 'city',
        tags: [newProps.location.query.professionals_city]
      };
      this.props.searchAddress(searchQuery);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const nameWasChanged = (prevState.companyNameForFilter !== this.state.companyNameForFilter);

    if (nameWasChanged && (!this.state.companyNameForFilter.length || this.state.companyNameForFilter.length > 2)) {
      const { property_city, professionals_city } = this.props.location.query;
      const isBrokerCommunity = this.checkIsBrokerCommunity();
      const isRealtorsAssociation = this.checkIsRealtorsAssociation();

      this.query = {
        ...this.query,
        property_city: property_city || undefined,
        city: professionals_city || undefined,
        name: this.state.companyNameForFilter || undefined,
        is_community_member: isBrokerCommunity || undefined,
        is_association_member: isRealtorsAssociation || undefined,
        $skip: 0,
        type: this.state.currentCompanyType,
        ...(professionals_city || property_city ? { $skip: undefined, $limit: undefined } : {})
      };

      this.loadProfessionals(this.query);
    }
  }

  componentWillUnmount() {
    if (!this.props.isMobile) {
      window.removeEventListener('resize', this.throttledCalcSwitchButtonsCount);
    }
  }

  checkIsBrokerCommunity = (props = this.props) => get(props, 'location.pathname', '').includes('broker');
  checkIsRealtorsAssociation = (props = this.props) => get(props, 'location.pathname', '').includes(ROUTES.REALTORS_ASSOCIATION);
  checkIsProfessionals = (props = this.props) => get(props, 'location.pathname', '').includes('professionals')
    && !this.checkIsBrokerCommunity(props) && !this.checkIsRealtorsAssociation(props);

  formSubmitHandler = (formValues) => {
    const { captcha } = this.state;

    this.props.sendProfessionalsContactUsForm({
      captcha,
      ...formValues
    });
  };

  openFormModal = () => this.props.openProfessionalsContactModal();

  closeFormModal = () => {
    document.body.style.overflow = '';
    this.props.closeProfessionalsContactModal();
  };

  handleChangeName = (evt) => {
    const { value = '' } = evt.target;
    this.setState({ companyNameForFilter: value });
  };

  handleSwitchType = (evt) => {
    const { professionals_city, property_city } = this.props.location.query;

    const type = (typeof evt === 'string') ? evt : evt.target.value;
    const query = {
      professionals_city,
      property_city,
      type
    };
    this.setState({ currentCompanyType: type });
    browserHistory.push({
      pathname: appendLocale(this.props.locale, '/professionals/search'),
      query
    });
    this.query = {
      ...this.query,
      city: professionals_city || undefined,
      property_city: property_city || undefined,
      name: this.state.companyNameForFilter || undefined,
      $skip: 0,
      ...(professionals_city || property_city ? { $skip: undefined, $limit: undefined } : {}),
      type
    };

    this.loadProfessionals(this.query);
  };

  loadMoreProfessionals = () => {
    if (!this.props.professionalsLoaded || this.props.professionalsLoading) { return; }
    const { professionals_city, property_city } = this.props.location.query;

    this.query = {
      ...this.query,
      city: professionals_city || undefined,
      property_city: property_city || undefined,
      $skip: this.query.$skip + limit,
      ...(professionals_city || property_city ? { $skip: undefined, $limit: undefined } : {})
    };
    this.loadProfessionals(this.query);
  };

  loadProfessionals = (query) => {
    this.props.receiveProfessionals(query);
  };

  fetchReCaptchaToken = (token) => this.setState({ captcha: token });

  calcSwitchButtonsCount = () => {
    if (!this.switcher) { return; }

    const { clientWidth, children = [] } = this.switcher || {};
    let width = 0;
    let count = 0;

    // for (let idx = 0; idx < this.state.companiesTypes.length + 1; idx += 1) {
    for (let idx = 0; idx < this.state.companiesTypes.length; idx += 1) {
      const currentEl = children[idx];
      width += currentEl.clientWidth + 20;

      if (width > clientWidth - 110) { break; }
      if (!currentEl || !currentEl.value) {
        count += 1;
        break;
      }

      count += 1;
    }

    this.setState({ maxSwitchButtonsCount: count });
  };

  classNameHeader = () => {
    switch (this.props.routetype) {
      case 'moving': return styles.moving;
      case 'mortgage': return styles.mortgage;
      case 'designer': return styles.designer;
      case 'lawyer': return styles.lawyer;
      case 'photographer': return styles.photographer;
      case 'appraisal': return styles.appraisal;

      default: return styles.realEstateAgency;
    }
  }

  renderHelmet = () => {
    const { route } = this.props;
    const isRealtorsAssociation = this.checkIsRealtorsAssociation();
    const isProfessionals = this.checkIsProfessionals();

    const contentImage = isRealtorsAssociation
      ? REALTORS_ASSOCIATION_LOGO
      : BROKER_COMPANY_LOGO;

    const params = [
      { property: 'og:image', content: contentImage },
      { property: 'og:image:width', content: 250 },
      { property: 'og:image:height', content: 250 },
      { name: 'image', itemProp: 'image', content: contentImage }
    ];

    return (
      <Helmet
        titleTemplate={this.props.translate('title', 'titleTemplate')()}
        title={this.props.translate('helmet', this.checkIsBrokerCommunity() ? 'brokerCommunity' : 'professionals')()}
        meta={[
          { property: 'og:url', content: `${shareUrl}${route}` },
          { property: 'og:title', content: `${this.props.translate('helmet', 'professionals')} | \u202BOnMap` },
          ...(!isProfessionals ? params : []),
          ...((this.props.locale !== language.default) ? [{ name: 'robots', content: 'noindex,nofollow' }] : [])
        ]}
      />
    );
  };

  renderProfessionalsNotFound = () => {
    const handleClick = () => {
      if (this.checkIsBrokerCommunity()) {
        browserHistory.push(appendLocale(this.props.locale, '/professionals/broker'));
      } else if (this.checkIsRealtorsAssociation()) {
        browserHistory.push(appendLocale(this.props.locale, `/${ROUTES.REALTORS_ASSOCIATION}`));
      } else {
        browserHistory.push(appendLocale(this.props.locale, '/professionals'));
      }
    };

    return (
      <div className={styles.notFoundWrapper}>
        <div className={styles.notFoundTitle}>{this.props.translate('professionals', 'notFoundTitle')}</div>
        <div className={styles.notFoundDescription}>{this.props.translate('professionals', 'notFoundDescription')}</div>
        <div className={styles.showAllWrapper}>
          <span>{this.props.translate('professionals', 'or')}</span>
          <button
            className={styles.showAllProfessionals}
            onClick={handleClick}
          >
            {this.props.translate('professionals', 'showAllProfessionals')}
          </button>
        </div>
      </div>
    );
  }

  renderTitle = () => (
      <h1 className={styles.pageTitle}>
        {this.props.translate('title', this.checkIsBrokerCommunity() ? 'brokerCommunity' : 'professionals')}
      </h1>
    );

  renderNameInput = () => (
    <div className={styles.nameInputWrapper}>
      <div className={styles.searchInputLabel}>
        {this.props.translate('professionals', 'filterByName')}
      </div>
      <input
        type="text"
        className="form-control"
        placeholder={this.props.translate('realEstateDetails', 'companyName')}
        value={this.state.companyNameForFilter}
        onChange={this.handleChangeName}
      />
    </div>
  );

  renderSwitchButton = (button) => (
    <button
      key={button.type}
      onClick={this.handleSwitchType}
      value={button.type}
      className={cx({ [styles.active]: this.state.currentCompanyType === button.type })}
    >
      {button.title}
    </button>
  );

  renderGetHereButton = () => (
    <button
      className={styles.getHereButton}
      onClick={this.openFormModal}
    >
      {this.props.translate('professionals', 'joinUs')}
    </button>
  );

  renderMobileSwitcher = () => {
    const options = [
      // { type: 'all', title: this.props.translate('professionalsTypes', 'all') },
      ...this.state.companiesTypes
    ]
      .map(it => ({ id: it.type, label: it.title }));
    const selected = options.find(({ id }) => id === this.state.currentCompanyType);
    return (
      <div className={styles.dropdownWrapper}>
        <OMDropdown
          options={options}
          selectedOption={selected}
          handleSelect={this.handleSwitchType}
          callToAction={this.props.translate('professionals', 'selectCategory').toString()}
        />
      </div>
    );
  };

  renderSwitcher = () => {
    if (!this.checkIsProfessionals()) { return null; }

    const switchButtons = [
      // { type: 'all', title: this.props.translate('professionalsTypes', 'all') },
      ...this.state.companiesTypes
    ];

    const mainButtons = switchButtons.splice(0, this.state.maxSwitchButtonsCount);

    const additionalOptions = switchButtons.map(it => ({ id: it.type, label: it.title }));
    const selected = additionalOptions.find(({ id }) => id === this.state.currentCompanyType);
    return (
      <div className={styles.switchTypes} ref={ref => { this.switcher = ref; }}>
        {mainButtons.map(this.renderSwitchButton)}
        {!!additionalOptions.length && (
          <OMDropdown
            options={additionalOptions}
            selectedOption={selected}
            handleSelect={this.handleSwitchType}
            callToAction={this.props.translate('header', 'more').toString()}
            isTab
          />
        )}
      </div>
    );
  };

  renderProfessionalContactUs = () => (
    <div className={styles.contactUsWrapper}>
      <ProfessionalContactUs
        fetchReCaptchaToken={this.fetchReCaptchaToken}
        locale={this.props.locale}
        translate={this.props.translate}
        onSubmit={this.formSubmitHandler}
        isRtl={this.props.isRtl}
        formSubmitting={this.props.formSubmitting}
        formSubmitted={this.props.formSubmitted}
        formError={this.props.formError}
        showMessage={this.props.openMessageModal}
        closeModal={this.closeFormModal}
        isMobile={this.props.isMobile}
        className="professionalsPage"
      />
    </div>
  );

  renderProfessionalsDescription = (currentCompanyType = this.state.currentCompanyType) => {
    const description = getProfessionalDescription(currentCompanyType, this.props.locale);
    const title = currentCompanyType === 'all'
      ? this.props.translate('title', 'professionals')
      : this.props.translate('professionalsTypes', currentCompanyType);

    const TitleTag = (currentCompanyType === 'all') ? 'div' : 'h2';

    return (
      <div className={styles.flexContainerWrapper}>
        <div className={styles.flexContainer}>
          <DescriptionTextBlock
            className="professionalDescription additionalBlock"
            locale={this.props.locale}
            contentText={`${description.col1}\n\n${description.col2}`}
            translate={this.props.translate}
            title={title.toString()}
            titleTag={TitleTag}
            isTitleTranslated
          />
        </div>
      </div>
    );
  };

  render() {
    const searchInputPlaceholder = this.props.translate('professionals', 'searchInputPlaceholder');
    const isSearchedProfessionals = !!this.props.location.query.professionals_city;
    const isSearchedProperty = !!this.props.location.query.property_city;
    const professionalsNotFound = ((isSearchedProfessionals || isSearchedProperty)
      && this.props.professionals && !this.props.professionals.length);
    const isRealtorsAssociation = this.checkIsRealtorsAssociation();
    const isBroker = this.checkIsBrokerCommunity();

    return (
      <div className={cx(styles.professionalsPage, { [styles.brokerPage]: isBroker || isRealtorsAssociation })}>
        <NotificationNoCompany />
        {this.renderHelmet()}
        {isBroker &&
        <CompanyInfoBlock
          translate={this.props.translate('companyPage')}
          title={BROKER_COMPANY_NAME}
          phoneNumber={BROKER_COMPANY_PHONE}
          isMobile={this.props.isMobile}
          locale={this.props.locale}
          scrollToBlock="professionals-page-content"
          offset={474}
          isShowMoreInfo
        />}
        <Element name="professionals-page-content">
          {isBroker && !isRealtorsAssociation && (<BrokerCommunityContainer />)}
          {isRealtorsAssociation && (<RealtorsAssociationContainer />)}
          <div className={styles.contentPart}>
            <div
              className={
                cx(
                  styles.pageHeaderWrapper,
                    {
                      [styles.brokerHeader]: isBroker || isRealtorsAssociation,
                      [this.classNameHeader()]: !this.props.isMobile
                    }
                  )
              }
            >
              <div className={styles.pageHeader}>
                <div className={styles.titleWrapper}>
                  {this.checkIsProfessionals() && this.renderTitle()}
                  {!this.props.isMobile && !isBroker && !isRealtorsAssociation && this.renderGetHereButton()}
                </div>

                <div className={cx(
                    styles.searchWrapper, { [styles.searchBrokerWrapper]: isBroker || isRealtorsAssociation }
                  )}
                >
                  <div className={styles.searchInput}>
                    <div className={styles.searchInputLabel}>{this.props.translate('professionals', 'searchInputLabel')}</div>
                    <ProfessionalsPageSearchInput
                      suggestions={this.props.suggestions}
                      className={styles.inputWrapper}
                      city={this.props.location.query.professionals_city}
                      translate={this.props.translate}
                      locale={this.props.locale}
                      receiveSuggestions={this.props.receiveSuggestions}
                      placeholder={searchInputPlaceholder}
                      address={this.props.address}
                      searchAddress={this.props.searchAddress}
                      isBrokerCommunity={isBroker}
                      isRealtorsAssociation={isRealtorsAssociation}
                      query={this.props.location.query}
                      queryName="professionals_city"
                    />
                  </div>
                  <div className={styles.searchInput}>
                    <div className={styles.searchInputLabel}>{this.props.translate('professionals', 'propertyIn')}</div>
                    <ProfessionalsPageSearchInput
                      suggestions={this.props.suggestions}
                      className={styles.inputWrapper}
                      city={this.props.location.query.property_city}
                      translate={this.props.translate}
                      locale={this.props.locale}
                      receiveSuggestions={this.props.receiveSuggestions}
                      placeholder={searchInputPlaceholder}
                      address={this.props.address}
                      searchAddress={this.props.searchAddress}
                      isBrokerCommunity={isBroker}
                      isRealtorsAssociation={isRealtorsAssociation}
                      query={this.props.location.query}
                      queryName="property_city"
                    />
                  </div>
                  {this.renderNameInput()}
                  {!isBroker && !isRealtorsAssociation && this.props.isMobile && this.renderMobileSwitcher()}
                </div>
                {this.props.isMobile && !isBroker && !isRealtorsAssociation && this.renderGetHereButton()}
              </div>
            </div>
            {!this.props.isMobile && this.renderSwitcher()}

            {!isBroker && !isRealtorsAssociation && this.renderProfessionalsDescription()}

            <ProfessionalsList
              isMobile={this.props.isMobile}
              professionals={this.props.professionals}
              translate={this.props.translate}
              locale={this.props.locale}
              isProfessionals={this.checkIsProfessionals()}
              isBroker={isBroker || isRealtorsAssociation}
              companyNameForFilter={this.state.companyNameForFilter}
              loadMore={this.loadMoreProfessionals}
              hasMore={this.props.professionalsCount > this.props.professionals.length}
              currentCompanyType={this.state.currentCompanyType}
              isMobal
            />

            {professionalsNotFound && this.renderProfessionalsNotFound()}
          </div>
          {!isBroker && !isRealtorsAssociation && this.renderProfessionalContactUs()}

          {(!isBroker && !isRealtorsAssociation) && (
            <div className={styles.bottomText}>
              {this.renderProfessionalsDescription('all')}
            </div>
          )}
        </Element>
        <Modal
          className="contactProfessionalsModal"
          isOpen={this.props.professionalContactFormIsOpen}
          onRequestClose={this.closeFormModal}
          translate={this.props.translate}
          isRtl={this.props.isRtl}
          withoutTitle
        >
          <ProfessionalContactUs
            fetchReCaptchaToken={this.fetchReCaptchaToken}
            locale={this.props.locale}
            translate={this.props.translate}
            onSubmit={this.formSubmitHandler}
            isRtl={this.props.isRtl}
            formSubmitting={this.props.formSubmitting}
            formSubmitted={this.props.formSubmitted}
            formError={this.props.formError}
            showMessage={this.props.openMessageModal}
            isMobile={this.props.isMobile}
            closeModal={this.closeFormModal}
            className="professionalsPageModal"
            isModal
          />
        </Modal>
      </div>
    );
  }
}

ProfessionalsPage.propTypes = {
  isMobile: PropTypes.bool,
  isRtl: PropTypes.bool,
  professionals: PropTypes.array,
  professionalsCount: PropTypes.number,
  suggestions: PropTypes.array,
  locale: PropTypes.string,
  translate: PropTypes.func,
  route: PropTypes.any,
  receiveProfessionals: PropTypes.func,
  receiveSuggestions: PropTypes.func,
  location: PropTypes.object,
  address: PropTypes.array,
  searchAddress: PropTypes.func,
  clearProfessionals: PropTypes.func,
  openProfessionalsContactModal: PropTypes.func,
  closeProfessionalsContactModal: PropTypes.func,
  sendProfessionalsContactUsForm: PropTypes.func,
  formSubmitted: PropTypes.bool,
  formSubmitting: PropTypes.bool,
  formError: PropTypes.object,
  professionalContactFormIsOpen: PropTypes.bool,
  openMessageModal: PropTypes.func,
  professionalsLoading: PropTypes.bool,
  professionalsLoaded: PropTypes.bool,
  routetype: PropTypes.string
};

const mapStateToProps = state => ({
  isRtl: checkRtlLanguage(state.lang.locale),
  professionals: state.professionals.professionals,
  professionalsLoading: state.professionals.loading,
  professionalsLoaded: state.professionals.loaded,
  professionalsCount: state.professionals.professionalsCount,
  suggestions: state.suggestions.suggestions,
  translate: getLang(state.lang, 'static'),
  address: state.searchAddressLocus.address,
  formSubmitted: state.professionals.formSubmitted,
  formSubmitting: state.professionals.formSubmitting,
  formError: state.professionals.formError,
  professionalContactFormIsOpen: state.professionals.professionalContactFormIsOpen,
  routetype: state.routing.locationBeforeTransitions.query.type
});

const mapDispatchToProps = {
  receiveProfessionals,
  clearProfessionals,
  receiveSuggestions,
  searchAddress,
  openProfessionalsContactModal,
  closeProfessionalsContactModal,
  sendProfessionalsContactUsForm,
  openMessageModal
};

const asyncData = [
  {
    key: 'professionals',
    promise: ({ location, store: { dispatch } }) =>
      dispatch(receiveProfessionals(buildProfessionalsQuery(location)))
  }
];

export default compose(
  asyncConnect(asyncData),
  connect(mapStateToProps, mapDispatchToProps)
)(withPageWrapper(ProfessionalsPage));
